import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Users, BookOpen, ScrollText, Settings2, UploadCloud } from "lucide-react";

const emptyPatient = {
  name: "", age: "", context: "", reason: "", personality: "", tone: "",
  backstory: "", objectives: "", allowed_topics: "", blocked_topics: "",
  difficulty: "medio", stay_in_character: "Mantente en personaje pase lo que pase.", avatar: "",
};

function PatientDialog({ trigger, initial, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyPatient);
  useEffect(() => {
    if (open) setF(initial ? {
      ...initial,
      age: initial.age ?? "", system_prompt: initial.system_prompt ?? "",
      allowed_topics: (initial.allowed_topics || []).join(", "),
      blocked_topics: (initial.blocked_topics || []).join(", "),
    } : emptyPatient);
  }, [open, initial]);

  const save = async () => {
    const payload = {
      ...f,
      age: f.age ? parseInt(f.age) : null,
      allowed_topics: f.allowed_topics ? f.allowed_topics.split(",").map((s) => s.trim()).filter(Boolean) : [],
      blocked_topics: f.blocked_topics ? f.blocked_topics.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    try {
      if (initial) await api.put(`/patients/${initial.id}`, payload);
      else await api.post("/patients", payload);
      toast.success("Paciente guardado");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto sepp-scroll">
        <DialogHeader><DialogTitle className="font-display">{initial ? "Editar" : "Nuevo"} paciente virtual</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Nombre</Label><Input data-testid="admin-patient-name-input" value={f.name} onChange={set("name")} /></div>
          <div className="space-y-1.5"><Label>Edad</Label><Input type="number" value={f.age} onChange={set("age")} /></div>
                    <div className="space-y-1.5 sm:col-span-2">
            <Label>Prompt del Sistema (Instrucciones completas del caso - Opcional)</Label>
            <p className="text-xs text-muted-foreground mb-1">Si llenas este campo, sobrescribirá la construcción automática del prompt (ignorando contexto, motivos, etc.) y usará exactamente lo que escribas aquí para configurar la IA.</p>
            <Textarea className="h-48" value={f.system_prompt} onChange={set("system_prompt")} placeholder="Eres un paciente virtual de 25 años que..." />
          </div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Contexto</Label><Input value={f.context} onChange={set("context")} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Motivo de consulta</Label><Input value={f.reason} onChange={set("reason")} /></div>
          <div className="space-y-1.5"><Label>Rasgos de personalidad</Label><Textarea value={f.personality} onChange={set("personality")} /></div>
          <div className="space-y-1.5"><Label>Tono y estilo</Label><Textarea value={f.tone} onChange={set("tone")} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Historia base del caso</Label><Textarea value={f.backstory} onChange={set("backstory")} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Objetivos pedagógicos</Label><Textarea value={f.objectives} onChange={set("objectives")} /></div>
          <div className="space-y-1.5"><Label>Temas permitidos (coma)</Label><Input value={f.allowed_topics} onChange={set("allowed_topics")} /></div>
          <div className="space-y-1.5"><Label>Temas bloqueados (coma)</Label><Input value={f.blocked_topics} onChange={set("blocked_topics")} /></div>
          <div className="space-y-1.5">
            <Label>Dificultad</Label>
            <Select value={f.difficulty} onValueChange={(v) => setF((s) => ({ ...s, difficulty: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fácil">Fácil</SelectItem>
                <SelectItem value="medio">Medio</SelectItem>
                <SelectItem value="difícil">Difícil</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>URL de avatar</Label><Input value={f.avatar} onChange={set("avatar")} /></div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Instrucciones de permanencia en personaje</Label>
            <Textarea data-testid="admin-patient-prompt-textarea" value={f.stay_in_character} onChange={set("stay_in_character")} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={!f.name} data-testid="admin-save-patient-button">Guardar paciente</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const emptyExercise = { title: "", description: "", objectives: "", patient_id: "", max_attempts: 3, difficulty: "medio", active: true };

function ExerciseDialog({ trigger, initial, patients, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyExercise);
  useEffect(() => { if (open) setF(initial || emptyExercise); }, [open, initial]);

  const save = async () => {
    try {
      const payload = { ...f, max_attempts: parseInt(f.max_attempts) || 1 };
      if (initial) await api.put(`/exercises/${initial.id}`, payload);
      else await api.post("/exercises", payload);
      toast.success("Ejercicio guardado");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto sepp-scroll">
        <DialogHeader><DialogTitle className="font-display">{initial ? "Editar" : "Nuevo"} ejercicio</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Título</Label><Input value={f.title} onChange={set("title")} /></div>
          <div className="space-y-1.5"><Label>Descripción</Label><Textarea value={f.description} onChange={set("description")} /></div>
          <div className="space-y-1.5"><Label>Objetivos pedagógicos</Label><Textarea value={f.objectives} onChange={set("objectives")} /></div>
          <div className="space-y-1.5">
            <Label>Paciente virtual</Label>
            <Select value={f.patient_id} onValueChange={(v) => setF((s) => ({ ...s, patient_id: v }))}>
              <SelectTrigger><SelectValue placeholder="Selecciona un paciente" /></SelectTrigger>
              <SelectContent>
                {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Intentos máximos</Label><Input type="number" min={1} value={f.max_attempts} onChange={set("max_attempts")} /></div>
            <div className="space-y-1.5">
              <Label>Dificultad</Label>
              <Select value={f.difficulty} onValueChange={(v) => setF((s) => ({ ...s, difficulty: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fácil">Fácil</SelectItem>
                  <SelectItem value="medio">Medio</SelectItem>
                  <SelectItem value="difícil">Difícil</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={f.active} onCheckedChange={(v) => setF((s) => ({ ...s, active: v }))} />
            <Label>Activo (visible para alumnos)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={!f.title || !f.patient_id}>Guardar ejercicio</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const [patients, setPatients] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [settings, setSettings] = useState(null);

  const loadAll = async () => {
    const [p, e, s, a, cfg] = await Promise.all([
      api.get("/patients"), api.get("/exercises"), api.get("/supervisor/sessions"),
      api.get("/admin/audit"), api.get("/admin/settings"),
    ]);
    setPatients(p.data); setExercises(e.data); setSessions(s.data); setAudit(a.data); setSettings(cfg.data);
  };
  useEffect(() => { loadAll(); }, []);

  const delPatient = async (id) => { await api.delete(`/patients/${id}`); toast.success("Paciente eliminado"); loadAll(); };
  const delExercise = async (id) => { await api.delete(`/exercises/${id}`); toast.success("Ejercicio eliminado"); loadAll(); };
  const delSession = async (id) => { await api.delete(`/admin/sessions/${id}`); toast.success("Diálogo eliminado"); loadAll(); };
  const publishExercise = async (id) => {
    try {
      const { data } = await api.post(`/exercises/${id}/publish`);
      toast.success(`Versión ${data.version} publicada`, { description: "Las sesiones nuevas usarán esta versión." });
      loadAll();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const runRetention = async () => {
    try {
      const { data } = await api.post("/admin/retention/run");
      toast.success(`Purga ejecutada: ${data.deleted} diálogo(s) eliminado(s)`);
      loadAll();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const saveSettings = async (section, payload) => {
    try {
      await api.put("/admin/settings", { [section]: payload });
      toast.success("Configuración guardada");
      loadAll();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  return (
    <AppShell title="Administración">
      <Tabs defaultValue="patients">
        <TabsList className="mb-6">
          <TabsTrigger value="patients" data-testid="tab-patients"><Users className="mr-1.5 h-4 w-4" />Pacientes</TabsTrigger>
          <TabsTrigger value="exercises" data-testid="tab-exercises"><BookOpen className="mr-1.5 h-4 w-4" />Ejercicios</TabsTrigger>
          <TabsTrigger value="dialogs" data-testid="tab-dialogs"><ScrollText className="mr-1.5 h-4 w-4" />Diálogos</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="mr-1.5 h-4 w-4" />Auditoría</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings"><Settings2 className="mr-1.5 h-4 w-4" />Configuración</TabsTrigger>
        </TabsList>

        {/* PACIENTES */}
        <TabsContent value="patients">
          <div className="mb-4 flex justify-end">
            <PatientDialog onSaved={loadAll} trigger={<Button data-testid="admin-add-patient-button"><Plus className="mr-2 h-4 w-4" />Nuevo paciente</Button>} />
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {patients.map((p) => (
              <div key={p.id} className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-display font-semibold">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.age ? `${p.age} años · ` : ""}{p.difficulty}</p>
                  </div>
                  <div className="flex gap-1">
                    <PatientDialog initial={p} onSaved={loadAll} trigger={<Button size="icon" variant="ghost"><Pencil className="h-4 w-4" /></Button>} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="icon" variant="ghost"><Trash2 className="h-4 w-4 text-destructive" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>¿Eliminar a {p.name}?</AlertDialogTitle>
                          <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => delPatient(p.id)}>Eliminar</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{p.reason}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* EJERCICIOS */}
        <TabsContent value="exercises">
          <div className="mb-4 flex justify-end">
            <ExerciseDialog patients={patients} onSaved={loadAll} trigger={<Button><Plus className="mr-2 h-4 w-4" />Nuevo ejercicio</Button>} />
          </div>
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Título</TableHead><TableHead>Paciente</TableHead><TableHead>Versión</TableHead><TableHead>Intentos</TableHead>
                <TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {exercises.map((ex) => (
                  <TableRow key={ex.id}>
                    <TableCell className="font-medium">{ex.title}</TableCell>
                    <TableCell>{ex.patient_name}</TableCell>
                    <TableCell>
                      {ex.published_at
                        ? <Badge variant="outline" className="border-teal-300 bg-teal-50 text-teal-700">v{ex.version || 1}</Badge>
                        : <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">Borrador</Badge>}
                    </TableCell>
                    <TableCell>{ex.max_attempts}</TableCell>
                    <TableCell>{ex.active
                      ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">Activo</Badge>
                      : <Badge variant="outline" className="text-slate-400">Inactivo</Badge>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => publishExercise(ex.id)} data-testid="publish-exercise-button">
                          <UploadCloud className="mr-1 h-3.5 w-3.5" />Publicar
                        </Button>
                        <ExerciseDialog patients={patients} initial={ex} onSaved={loadAll} trigger={<Button size="icon" variant="ghost"><Pencil className="h-4 w-4" /></Button>} />
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button size="icon" variant="ghost"><Trash2 className="h-4 w-4 text-destructive" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>¿Eliminar ejercicio?</AlertDialogTitle></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => delExercise(ex.id)}>Eliminar</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* DIALOGOS */}
        <TabsContent value="dialogs">
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <Table>
              <TableHeader><TableRow>
                <TableHead>ID</TableHead><TableHead>Matrícula</TableHead><TableHead>Ejercicio</TableHead>
                <TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {sessions.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-xs text-primary">{s.public_id}</TableCell>
                    <TableCell>{s.student_matricula}</TableCell>
                    <TableCell>{s.exercise_title}</TableCell>
                    <TableCell><StatusBadge status={s.status} /></TableCell>
                    <TableCell className="text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="icon" variant="ghost"><Trash2 className="h-4 w-4 text-destructive" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>¿Borrar diálogo {s.public_id}?</AlertDialogTitle>
                            <AlertDialogDescription>Se eliminarán todos los mensajes de forma permanente.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => delSession(s.id)}>Eliminar</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* AUDITORIA */}
        <TabsContent value="audit">
          <div className="overflow-hidden rounded-xl border border-border bg-card" data-testid="admin-audit-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Evento</TableHead><TableHead>Actor</TableHead><TableHead>Rol</TableHead>
                <TableHead>Detalles</TableHead><TableHead>Fecha</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {audit.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell><Badge variant="outline" className="font-mono text-xs">{a.event}</Badge></TableCell>
                    <TableCell className="text-sm">{a.actor}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{a.actor_role}</TableCell>
                    <TableCell className="max-w-xs truncate text-xs text-muted-foreground">{JSON.stringify(a.details)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{(a.timestamp || "").slice(0, 19).replace("T", " ")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* CONFIGURACION */}
        <TabsContent value="settings">
          {settings && <SettingsPanel settings={settings} onSave={saveSettings} onRunRetention={runRetention} />}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

function SettingsPanel({ settings, onSave, onRunRetention }) {
  const [smtp, setSmtp] = useState({ ...settings.smtp, password: "" });
  const [pol, setPol] = useState(settings.policies);
  
  const [aiProvider, setAiProvider] = useState(settings.ai_config?.active_provider || "deepseek");
  const [aiKeys, setAiKeys] = useState({
    openai: "", anthropic: "", deepseek: ""
  });
  const [aiModels, setAiModels] = useState({
    openai: settings.ai_config?.providers?.openai?.model || "gpt-4o",
    anthropic: settings.ai_config?.providers?.anthropic?.model || "claude-3-5-sonnet-20241022",
    deepseek: settings.ai_config?.providers?.deepseek?.model || "deepseek-reasoner"
  });

  const saveAI = () => {
    onSave("ai_config", {
      active_provider: aiProvider,
      providers: {
        openai: { model: aiModels.openai, api_key: aiKeys.openai },
        anthropic: { model: aiModels.anthropic, api_key: aiKeys.anthropic },
        deepseek: { model: aiModels.deepseek, api_key: aiKeys.deepseek },
      }
    });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-border bg-card p-6">
        <p className="font-display font-semibold">Motor de Inteligencia Artificial</p>
        <p className="mb-4 text-xs text-muted-foreground">Selecciona qué proveedor quieres que interprete al paciente virtual e ingresa su respectiva clave y modelo.<br/><strong className="text-amber-600 mt-1 block">Nota importante: La API Key de OpenAI es obligatoria si deseas usar la funcionalidad de voz (Voz del Paciente), sin importar qué proveedor elijas para pensar el texto.</strong></p>
        
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Proveedor Activo</Label>
            <Select value={aiProvider} onValueChange={setAiProvider}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI (ChatGPT)</SelectItem>
                <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                <SelectItem value="deepseek">DeepSeek</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="rounded-md bg-muted/50 p-4 space-y-3">
            <div className="space-y-1.5">
              <Label>Modelo de {aiProvider}</Label>
              <Input 
                value={aiModels[aiProvider]} 
                onChange={(e) => setAiModels({ ...aiModels, [aiProvider]: e.target.value })} 
                placeholder={aiProvider === "openai" ? "gpt-4o" : aiProvider === "anthropic" ? "claude-3-5-sonnet-20241022" : "deepseek-reasoner"}
              />
            </div>
            <div className="space-y-1.5">
              <Label>API Key de {aiProvider}</Label>
              <Input 
                type="password" 
                placeholder={"sk-..."} 
                value={aiKeys[aiProvider]} 
                onChange={(e) => setAiKeys({ ...aiKeys, [aiProvider]: e.target.value })} 
              />
            </div>
          </div>
          <Button onClick={saveAI}>Guardar Configuración de IA</Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <p className="font-display font-semibold">Correo SMTP</p>
        <p className="mb-4 text-xs text-muted-foreground">Envío de códigos OTP. {settings.smtp.password_configured ? "✓ Configurado." : "Sin configurar: el código se muestra en pantalla (modo prueba)."}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5 col-span-2"><Label>Host</Label><Input value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Puerto</Label><Input type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value) })} /></div>
          <div className="space-y-1.5">
            <Label>TLS</Label>
            <Select value={smtp.tls_mode} onValueChange={(v) => setSmtp({ ...smtp, tls_mode: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="starttls">STARTTLS (587)</SelectItem>
                <SelectItem value="ssl">SSL (465)</SelectItem>
                <SelectItem value="plain">Sin cifrado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 col-span-2"><Label>Usuario</Label><Input value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} /></div>
          <div className="space-y-1.5 col-span-2"><Label>Remitente (From)</Label><Input value={smtp.from_address} onChange={(e) => setSmtp({ ...smtp, from_address: e.target.value })} /></div>
          <div className="space-y-1.5 col-span-2"><Label>Contraseña</Label><Input type="password" placeholder={settings.smtp.password_configured ? "•••••• (sin cambios)" : ""} value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} /></div>
        </div>
        <Button className="mt-4" onClick={() => onSave("smtp", smtp)} data-testid="save-smtp-button">Guardar SMTP</Button>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 lg:col-span-2">
        <p className="font-display font-semibold">Políticas</p>
        <p className="mb-4 text-xs text-muted-foreground">Visibilidad de matrícula, PIN y retención.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <Label className="text-sm">Matrícula completa en PDF</Label>
            <Switch checked={pol.show_full_matricula_pdf} onCheckedChange={(v) => setPol({ ...pol, show_full_matricula_pdf: v })} />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <Label className="text-sm">Matrícula completa en verificación pública (QR)</Label>
            <Switch checked={pol.show_full_matricula_shared} onCheckedChange={(v) => setPol({ ...pol, show_full_matricula_shared: v })} />
          </div>
          <div className="space-y-1.5"><Label>Longitud del PIN</Label><Input type="number" min={4} max={10} value={pol.pin_length} onChange={(e) => setPol({ ...pol, pin_length: parseInt(e.target.value) })} /></div>
          <div className="space-y-1.5"><Label>Retención (días, 0 = indefinida)</Label><Input type="number" min={0} value={pol.retention_days} onChange={(e) => setPol({ ...pol, retention_days: parseInt(e.target.value) })} /></div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button onClick={() => onSave("policies", pol)}>Guardar políticas</Button>
          <Button variant="outline" onClick={onRunRetention} data-testid="run-retention-button">
            Ejecutar purga ahora
          </Button>
          <span className="text-xs text-muted-foreground">La purga automática corre cada hora si la retención es &gt; 0.</span>
        </div>
      </div>
    </div>
  );
}
